import { useMutation } from '@apollo/client';
import {
  ADD_PIPELINE_TAG_QUERY,
  DELETE_PIPELINE_TAG_QUERY,
  FETCH_PIPELINE_QUERY,
} from '../../utils/model_server';

export const usePipelineTags = ({ pipelineId, pipeline }) => {
  const [addPipelineTag] = useMutation(ADD_PIPELINE_TAG_QUERY, {
    update: (cache, { data: { insert_pipeline_tag_one } }) => {
      const existingPipelines = cache.readQuery({
        query: FETCH_PIPELINE_QUERY,
        variables: { pipeline_id: pipelineId },
      });

      const newPipelines = (existingPipelines?.pipeline ?? []).map(
        existingPipeline => insert_pipeline_tag_one.pipeline_id === existingPipeline.id
          ? { ...existingPipeline, pipeline_tags: existingPipeline.pipeline_tags.concat([insert_pipeline_tag_one]) }
          : existingPipeline
      );

      cache.writeQuery({
        query: FETCH_PIPELINE_QUERY,
        variables: { pipeline_id: pipelineId },
        data: { pipeline: newPipelines },
      });
    },
  });

  const handleAddTag = (newTag) => {
    if (!pipeline || pipeline.pipelineTags.some(({ tag }) => tag.tag === newTag)) {
      return;
    }

    addPipelineTag({
      variables: { pipeline_id: pipeline.id, tag_name: newTag },
      optimisticResponse: {
        insert_pipeline_tag_one: {
          id: `temp-pipeline-tag-id__${newTag}`,
          pipeline_pk_id: pipeline.id,
          __typename: 'pipeline_tag',
          tag_pk_id: `temp-tag-id__${newTag}`,
          tag: {
            id: `temp-tag-id__${newTag}`,
            tag: newTag,
            __typename: 'tag',
          },
        },
      },
    });
  };
  
  const [deletePipelineTag] = useMutation(DELETE_PIPELINE_TAG_QUERY, {
    update: (cache, { data: { delete_pipeline_tag } }) => {
      const deletedPipelineTagIDs = delete_pipeline_tag.returning.map(({ id }) => id);

      const existingPipelines = cache.readQuery({
        query: FETCH_PIPELINE_QUERY,
        variables: { pipeline_id: pipelineId },
      });

      const newPipelines = existingPipelines.pipeline.map(existingPipeline => ({
        ...existingPipeline,
        pipeline_tags: existingPipeline.pipeline_tags.filter(({ id }) => !deletedPipelineTagIDs.includes(id)),
      }));

      cache.writeQuery({
        query: FETCH_PIPELINE_QUERY,
        variables: { pipeline_id: pipelineId },
        data: { pipeline: newPipelines },
      });
    },
  });

  const handleDeleteTag = (deletedTag) => {
    if (!pipeline) {
      return;
    }

    const pipelineTag = pipeline.pipelineTags.find(({ tag }) => tag.tag === deletedTag);
    if (!pipelineTag) {
      return;
    }

    deletePipelineTag({
      variables: { pipeline_tag_id: pipelineTag.id },
      optimisticResponse: {
        delete_pipeline_tag: {
          returning: [{ __typename: 'pipeline_tag', id: pipelineTag.id }],
          __typename: 'pipeline_tag_mutation_response',
        },
      },
    });
  };

  return {
    addPipelineTag: handleAddTag,
    deletePipelineTag: handleDeleteTag,
  }
}
